import React, { useCallback, forwardRef } from 'react';

import { Tooltip } from 'react-tooltip';

interface ButtonGroupItem {
  label: string;
  value: string | number | boolean;
  icon?: React.ReactNode;
}

interface ButtonProps {
  icon?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  tooltipText?: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  groupClassName?: string;
  ariaLabel?: string;
  variant?: 'default' | 'pink-bordered';
  disabled?: boolean;
  buttons: ButtonGroupItem[];
  checkedValue: string | number | boolean;

  [key: string]: unknown;
}

const textColors = {
  default: {
    active: 'text-white',
    color: 'text-gray-600',
  },
  ['pink-bordered']: {
    active: 'text-brand-950',
    color: 'text-brand-400',
  },
};

const bgColors = {
  default: {
    active: 'bg-brand-800',
    color: 'bg-gray-950',
  },
  ['pink-bordered']: {
    active: 'text-brand-950',
    color: 'text-brand-400',
  },
};

export const ButtonGroup = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      buttons,
      icon,
      onClick,
      tooltipText,
      tooltipPosition = 'top',
      className = '',
      groupClassName = '',
      checkedValue,
      ariaLabel = 'button group',
      variant = 'default',
      disabled,
      ...rest
    },
    ref,
  ) => {
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled) return;
        e.preventDefault();
        onClick?.(e);
      },
      [disabled, onClick],
    );

    const disabledClassName = disabled ? 'cursor-not-allowed opacity-30' : '';

    const commonClass =
      'flex cursor-pointer items-center justify-center transition-all duration-300 text-xs font-normal leading-4 ';

    const variantClasses = {
      default: `w-[${100 / buttons.length}%] h-[32px]`,
      // 'rounded-md border border-transparent-0 p-2 bg-transparent hover:border-pink-500 hover:bg-gray-900',
      ['pink-bordered']: '',
    };
    const variantGroupClasses = {
      default: 'h-8 border rounded-full border-brand-800 overflow-hidden',
      ['pink-bordered']: '',
    };

    const buttonClass = `${variantClasses[variant]} ${commonClass} ${disabledClassName} ${className}`;
    const groupClass = `${variantGroupClasses[variant]} ${commonClass} ${disabledClassName} ${groupClassName} `;

    return (
      <div className={groupClass} aria-label={ariaLabel}>
        {buttons.map((btn, idx) => {
          const textColorClass =
            btn.value === checkedValue ? textColors[variant].active : textColors[variant].color;
          const bgColorClass =
            btn.value === checkedValue ? bgColors[variant].active : bgColors[variant].color;

          return (
            <button
              key={idx}
              type="button"
              aria-label={ariaLabel}
              data-tooltip-id={tooltipText ? tooltipText : undefined}
              className={`${buttonClass} ${textColorClass} ${bgColorClass}`}
              ref={ref}
              onClick={handleClick}
              value={typeof btn.value === 'boolean' ? String(btn.value) : btn.value}
              {...rest}
            >
              {icon}
              {btn.label}
              {tooltipText && (
                <Tooltip id={tooltipText} className="max-w-[300px]" place={tooltipPosition}>
                  {tooltipText}
                </Tooltip>
              )}
            </button>
          );
        })}
      </div>
    );
  },
);
